import React, { useEffect, useState } from "react";
import api from "../../../api/client";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import TriStateToggle from "../../../components/common/TriStateToggle";
import { toast } from "sonner";
import ChemicalOptimizerPanel from "../ChemicalOptimizerPanel";
import { useAuth } from "../../../context/AuthContext";
import { Button } from "../../../components/ui/button";
import { Sparkles, Eraser } from "lucide-react";

const CIPA_HOURS_BY_GRAU = { 1: 8, 2: 12, 3: 16, 4: 20 };
const CIPA_SIGLA = "NR-05";

const CATEGORY_LABELS = {
  programa: "Programa / documento",
  laudo: "Laudo / relatório",
  treinamento: "Treinamento / capacitação",
  avaliacao: "Avaliação / inspeção",
  consultoria: "Consultoria / acompanhamento",
  assessoria: "Consultoria / acompanhamento",
  visita: "Visita",
  projeto: "Projeto / adequação",
  servico: "Serviço técnico",
  outro: "Outro",
};

const catalogKey = (item) => item?.catalog_id || item?.id || item?.sigla || `${item?.categoria || "servico"}:${item?.descricao || item?.nome_proposta || ""}`;
const proposalName = (item) => String(item?.nome_proposta || item?.descricao || item?.sigla || "").trim();
const fixedProposalName = (item) => String(item?.nome_proposta || item?.nome || "").trim();
const statusPriority = { included: 3, bonus: 2, not_included: 1 };

const preferExisting = (current, candidate) => {
  if (!current) return candidate;
  return (statusPriority[candidate?.status] || 0) > (statusPriority[current?.status] || 0) ? candidate : current;
};

export default function Step2Services({ data, setData }) {
  const { isAdmin } = useAuth();
  const [clientGrauRisco, setClientGrauRisco] = useState(null);
  const [clientNumFunc, setClientNumFunc] = useState(0);
  const [aiServicosBusy, setAiServicosBusy] = useState(false);

  const loadCatalog = async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/services"), api.get("/fixed-evaluations"),
      ]);
      const serviceRows = s.data || [];
      const fixedRows = f.data || [];

      setData((d) => {
        const newD = { ...d };

        // Reconciliamos SEMPRE pelos dados atuais do catálogo. Se um item mudar de
        // categoria, ele migra de bloco no wizard em vez de ficar preso na lista antiga.
        // A categoria define o fluxo/campos específicos (ex.: carga horária), mas nunca
        // reescreve o texto comercial mostrado ao cliente.
        const existingGeneral = d.programas_laudos || [];
        const existingTraining = d.treinamentos || [];
        const existingAll = [...existingGeneral, ...existingTraining];
        const currentById = new Map(serviceRows.map((x) => [x.id, x]));
        const currentBySigla = new Map(serviceRows.filter((x) => x.sigla).map((x) => [String(x.sigla).toUpperCase(), x]));
        const existingById = new Map();
        const existingBySigla = new Map();

        existingAll.forEach((item) => {
          if (item.catalog_id) existingById.set(item.catalog_id, preferExisting(existingById.get(item.catalog_id), item));
          if (item.sigla) {
            const key = String(item.sigla).toUpperCase();
            existingBySigla.set(key, preferExisting(existingBySigla.get(key), item));
          }
        });

        const matchCurrent = (item) => (
          (item.catalog_id && currentById.get(item.catalog_id))
          || (item.sigla && currentBySigla.get(String(item.sigla).toUpperCase()))
          || null
        );
        const priorFor = (row) => existingById.get(row.id) || (row.sigla ? existingBySigla.get(String(row.sigla).toUpperCase()) : null);

        const generalCatalog = serviceRows.filter((x) => x.categoria !== "treinamento");
        const trainingCatalog = serviceRows.filter((x) => x.categoria === "treinamento");

        const generalMerged = generalCatalog.map((x) => {
          const prior = priorFor(x);
          return {
            catalog_id: x.id,
            sigla: x.sigla || "",
            descricao: x.descricao,
            nome_proposta: x.nome_proposta || x.descricao,
            norma: x.norma_referencia,
            status: prior?.status || "not_included",
            custo: prior?.custo ?? x.custo_padrao,
            venda: prior?.venda ?? x.venda_padrao,
            categoria: x.categoria || "servico",
            quantidade: prior?.quantidade ?? (x.categoria === "visita" ? 1 : 1),
            unidade: x.categoria === "visita" ? "visita" : (prior?.unidade || "unidade"),
          };
        });
        existingGeneral.filter((item) => !matchCurrent(item)).forEach((item) => generalMerged.push(item));
        newD.programas_laudos = generalMerged;

        const trainingMerged = trainingCatalog.map((x) => {
          const prior = priorFor(x);
          return {
            catalog_id: x.id,
            sigla: x.sigla || "",
            descricao: x.descricao,
            nome_proposta: x.nome_proposta || x.descricao,
            status: prior?.status || "not_included",
            carga_horaria: Number(prior?.carga_horaria || 0) > 0 ? Number(prior.carga_horaria) : 4,
            custo: prior?.custo ?? x.custo_padrao,
            venda: prior?.venda ?? x.venda_padrao,
          };
        });
        existingTraining.filter((item) => !matchCurrent(item)).forEach((item) => trainingMerged.push(item));
        newD.treinamentos = trainingMerged;

        // Avaliações fixas têm identidade própria. catalog_id evita que uma simples
        // renomeação crie uma avaliação duplicada na proposta.
        const existingFixed = d.avaliacoes_fixas || [];
        const fixedById = new Map(existingFixed.filter((x) => x.catalog_id).map((x) => [x.catalog_id, x]));
        const fixedByName = new Map(existingFixed.map((x) => [String(x.nome || "").toLocaleLowerCase("pt-BR"), x]));
        const currentFixedIds = new Set(fixedRows.map((x) => x.id));
        const currentFixedNames = new Set(fixedRows.map((x) => String(x.nome || "").toLocaleLowerCase("pt-BR")));
        const fixedMerged = fixedRows.map((x) => {
          const prior = fixedById.get(x.id) || fixedByName.get(String(x.nome || "").toLocaleLowerCase("pt-BR"));
          return {
            catalog_id: x.id,
            nome: x.nome,
            nome_proposta: x.nome_proposta || x.nome,
            status: prior?.status || "not_included",
            quantidade: prior?.quantidade ?? 0,
            custo_unitario: prior?.custo_unitario ?? x.custo,
            venda_unitaria: prior?.venda_unitaria ?? x.venda,
            unidade: x.unidade,
            por_funcionario: !!x.por_funcionario,
            mensal: !!x.mensal,
          };
        });
        existingFixed.filter((item) => {
          if (item.catalog_id) return !currentFixedIds.has(item.catalog_id);
          return !currentFixedNames.has(String(item.nome || "").toLocaleLowerCase("pt-BR"));
        }).forEach((item) => fixedMerged.push(item));
        newD.avaliacoes_fixas = fixedMerged;

        return newD;
      });
    } catch (e) {
      toast.error("Falha ao carregar catálogo");
    }
  };

  useEffect(() => { loadCatalog(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data.client_id) { setClientGrauRisco(null); setClientNumFunc(0); return; }
    let cancelled = false;
    api.get(`/clients/${data.client_id}`).then((r) => {
      if (!cancelled) {
        setClientGrauRisco(r.data?.grau_risco ?? null);
        setClientNumFunc(r.data?.num_funcionarios ?? 0);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [data.client_id]);

  useEffect(() => {
    if (!clientNumFunc) return;
    setData((d) => {
      const list = d.avaliacoes_fixas || [];
      let changed = false;
      const newList = list.map((it) => {
        if (it.por_funcionario && it.quantidade !== clientNumFunc) {
          changed = true;
          return { ...it, quantidade: clientNumFunc };
        }
        return it;
      });
      return changed ? { ...d, avaliacoes_fixas: newList } : d;
    });
  }, [clientNumFunc, data.avaliacoes_fixas, setData]);

  useEffect(() => {
    if (!clientGrauRisco) return;
    const hours = CIPA_HOURS_BY_GRAU[clientGrauRisco];
    if (!hours) return;
    setData((d) => {
      const list = d.treinamentos || [];
      const idx = list.findIndex((t) => t.sigla === CIPA_SIGLA);
      if (idx < 0 || list[idx].carga_horaria === hours) return d;
      const newList = [...list];
      newList[idx] = { ...newList[idx], carga_horaria: hours };
      return { ...d, treinamentos: newList };
    });
  }, [clientGrauRisco, (data.treinamentos || []).length, setData]); // eslint-disable-line react-hooks/exhaustive-deps

  const updItem = (listKey, idx, key, value) => {
    setData((d) => {
      const list = [...(d[listKey] || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...d, [listKey]: list };
    });
  };

  const updAcomp = (k, v) => setData((d) => ({ ...d, acompanhamento: { ...d.acompanhamento, [k]: v } }));

  const setAvaliacoesQuimicas = (items) => {
    setData((d) => {
      const prev = d.avaliacoes_quimicas || [];
      const merged = (items || []).map((it) => {
        const existing = prev.find((p) => p.chemical_id === it.chemical_id);
        return existing ? { ...it, status: existing.status, venda: existing.venda } : it;
      });
      return { ...d, avaliacoes_quimicas: merged };
    });
  };

  const setChemSolicitadas = (names) => setData((d) => ({ ...d, chem_avaliacoes_solicitadas: names }));
  const updQuim = (idx, key, val) => setData((d) => {
    const list = [...(d.avaliacoes_quimicas || [])];
    list[idx] = { ...list[idx], [key]: val };
    return { ...d, avaliacoes_quimicas: list };
  });
  const setForceNonOptimized = (v) => setData((d) => ({ ...d, chem_force_non_optimized: v }));

  const limparTudo = () => setData((d) => ({
    ...d,
    programas_laudos: (d.programas_laudos || []).map((x) => ({ ...x, status: "not_included" })),
    avaliacoes_fixas: (d.avaliacoes_fixas || []).map((x) => ({ ...x, status: "not_included" })),
    treinamentos: (d.treinamentos || []).map((x) => ({ ...x, status: "not_included" })),
  }));

  const sugerirServicos = async () => {
    if (!data.client_id) return toast.error("Selecione um cliente antes de sugerir serviços.");
    setAiServicosBusy(true);
    try {
      const r = await api.post("/proposals/ai/suggest", { proposal: data, campos: ["servicos"] });
      const siglas = (r.data?.sugestoes?.servicos || []).map((s) => String(s).toUpperCase());
      if (!siglas.length) return toast.error("A IA não sugeriu serviços.");
      const set = new Set(siglas);
      setData((d) => ({
        ...d,
        programas_laudos: (d.programas_laudos || []).map((p) => (set.has((p.sigla || "").toUpperCase()) ? { ...p, status: "included" } : p)),
        treinamentos: (d.treinamentos || []).map((t) => (set.has((t.sigla || "").toUpperCase()) ? { ...t, status: "included" } : t)),
      }));
      toast.success(`IA sugeriu ${siglas.length} serviço(s): ${siglas.join(", ")}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Falha ao sugerir com IA";
      toast.error(typeof msg === "string" ? msg : "Falha ao sugerir com IA");
    } finally { setAiServicosBusy(false); }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="wizard-step-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-medium">Serviços e Avaliações</h2>
          <p className="text-sm text-muted-foreground">Selecione o que será contratado ou concedido como bônus. A categoria define em qual bloco o item é configurado, mas o texto mostrado ao cliente vem literalmente do catálogo.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={sugerirServicos} disabled={aiServicosBusy} data-testid="ai-sugerir-servicos-btn">
            <Sparkles className="size-4 mr-1" /> {aiServicosBusy ? "Sugerindo..." : "Sugerir serviços (IA)"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={limparTudo} data-testid="limpar-selecao-btn">
            <Eraser className="size-4 mr-1" /> Limpar seleção
          </Button>
        </div>
      </div>

      <section className="space-y-3" data-testid="block-programas">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="label-tech">A. Serviços, documentos, laudos e projetos</h3>
            <p className="mt-1 text-xs text-muted-foreground">Itens do catálogo que não estão classificados como treinamento. O grupo não muda o nome exibido ao cliente.</p>
          </div>
          <a href="/cadastros/servicos" className="text-xs text-primary hover:underline">Gerenciar catálogo →</a>
        </div>
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-3 py-2 label-tech">Grupo</th>
                <th className="text-left px-3 py-2 label-tech">Como aparece na proposta</th>
                <th className="text-left px-3 py-2 label-tech">Referência</th>
                <th className="text-right px-3 py-2 label-tech">Qtd. visita</th>
                {isAdmin && <th className="text-right px-3 py-2 label-tech">Custo</th>}
                {isAdmin && <th className="text-right px-3 py-2 label-tech">Venda</th>}
                <th className="text-center px-3 py-2 label-tech">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(data.programas_laudos || []).map((p, idx) => {
                const key = catalogKey(p) || idx;
                const display = proposalName(p);
                return (
                  <tr key={key}>
                    <td className="px-3 py-2"><span className="rounded-md bg-muted px-2 py-1 text-xs">{CATEGORY_LABELS[p.categoria] || "Serviço"}</span></td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{display || "Serviço sem nome"}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{[p.sigla, p.descricao && p.descricao !== display ? p.descricao : null].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{p.norma || "—"}</td>
                    <td className="px-3 py-2 text-right">{<Input className="w-20 ml-auto text-right font-mono" aria-label={`Quantidade de ${p.nome_proposta || p.descricao}`} type="number" min="1" max="100000" value={p.quantidade || 1} onChange={(e) => updItem("programas_laudos", idx, "quantidade", Math.min(100000, Math.max(parseInt(e.target.value || 1, 10), 1)))} />}</td>
                    {isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={p.custo} onChange={(e) => updItem("programas_laudos", idx, "custo", parseFloat(e.target.value || 0))} /></td>}
                    {isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={p.venda} onChange={(e) => updItem("programas_laudos", idx, "venda", parseFloat(e.target.value || 0))} /></td>}
                    <td className="px-3 py-2 text-center"><TriStateToggle value={p.status} onChange={(v) => updItem("programas_laudos", idx, "status", v)} testidPrefix={`prog-${p.sigla || idx}`} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3" data-testid="block-acompanhamento">
        <h3 className="label-tech">B. Acompanhamento</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label className="label-tech">Nº Funcionários</Label><Input type="number" min="0" value={data.acompanhamento.num_funcionarios} onChange={(e) => updAcomp("num_funcionarios", parseInt(e.target.value || 0, 10))} data-testid="acomp-num-func" /></div>
          <div><Label className="label-tech">Visitas técnicas previstas</Label><Input type="number" min="0" value={data.acompanhamento.visitas_previstas} onChange={(e) => updAcomp("visitas_previstas", parseInt(e.target.value || 0, 10))} data-testid="acomp-visitas-num" /></div>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><span className="inline-block w-1 h-1 rounded-full bg-primary" />Valor por funcionário/mês, custo interno e valor por visita são aplicados automaticamente a partir das <a href="/configuracoes" className="text-primary underline">Configurações</a> (admin).</p>
      </section>

      <section className="space-y-3" data-testid="block-fixed-evals">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="label-tech">C. Avaliações Quantitativas Fixas</h3>
            <p className="mt-1 text-xs text-muted-foreground">Este catálogo é separado de treinamentos e usa o texto comercial exato cadastrado para cada avaliação.</p>
          </div>
          <a href="/cadastros/avaliacoes-fixas" className="text-xs text-primary hover:underline">Gerenciar catálogo →</a>
        </div>
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-muted/30"><tr><th className="text-left px-3 py-2 label-tech">Como aparece na proposta</th><th className="text-right px-3 py-2 label-tech">Qtd</th>{isAdmin && <th className="text-right px-3 py-2 label-tech">Custo Unit.</th>}{isAdmin && <th className="text-right px-3 py-2 label-tech">Venda Unit.</th>}<th className="text-center px-3 py-2 label-tech">Estado</th></tr></thead>
            <tbody className="divide-y divide-border">
              {(data.avaliacoes_fixas || []).map((f, idx) => (
                <tr key={f.catalog_id || f.nome || idx}>
                  <td className="px-3 py-2"><div className="font-medium">{fixedProposalName(f) || "Avaliação sem nome"}</div><div className="mt-0.5 text-xs text-muted-foreground">{f.nome && f.nome !== fixedProposalName(f) ? `${f.nome} · ` : ""}{f.unidade}{f.por_funcionario ? ` · auto ${clientNumFunc || 0} func.` : ""}{f.mensal ? ` · mensal × ${data.duracao_meses || 12}m` : ""}</div></td>
                  <td className="px-3 py-2 text-right"><Input className="w-20 ml-auto text-right font-mono" type="number" min="0" value={f.quantidade} onChange={(e) => updItem("avaliacoes_fixas", idx, "quantidade", parseInt(e.target.value || 0, 10))} disabled={!!f.por_funcionario} title={f.por_funcionario ? "Preenchido automaticamente pelo nº de funcionários do cliente" : ""} /></td>
                  {isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={f.custo_unitario} onChange={(e) => updItem("avaliacoes_fixas", idx, "custo_unitario", parseFloat(e.target.value || 0))} /></td>}
                  {isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={f.venda_unitaria} onChange={(e) => updItem("avaliacoes_fixas", idx, "venda_unitaria", parseFloat(e.target.value || 0))} /></td>}
                  <td className="px-3 py-2 text-center"><TriStateToggle value={f.status} onChange={(v) => updItem("avaliacoes_fixas", idx, "status", v)} testidPrefix={`fixed-${f.catalog_id || f.nome || idx}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3" data-testid="block-chemicals">
        <div className="flex items-baseline justify-between flex-wrap gap-2"><h3 className="label-tech">D. Avaliações Químicas</h3><a href="/cadastros/avaliacoes-quimicas" className="text-xs text-primary hover:underline" target="_blank" rel="noreferrer">Gerenciar catálogo →</a></div>
        <ChemicalOptimizerPanel value={data.avaliacoes_quimicas || []} onChange={setAvaliacoesQuimicas} selectedNames={data.chem_avaliacoes_solicitadas || []} onSelectedChange={setChemSolicitadas} forceNonOptimized={!!data.chem_force_non_optimized} onToggleForce={setForceNonOptimized} />
        {(data.avaliacoes_quimicas || []).length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden mt-2"><table className="w-full text-sm"><thead className="bg-muted/30"><tr><th className="text-left px-3 py-2 label-tech">Item</th>{isAdmin && <th className="text-right px-3 py-2 label-tech">Custo</th>}{isAdmin && <th className="text-right px-3 py-2 label-tech">Venda</th>}<th className="text-center px-3 py-2 label-tech">Estado</th></tr></thead><tbody className="divide-y divide-border">{(data.avaliacoes_quimicas || []).map((q, idx) => (<tr key={q.chemical_id}><td className="px-3 py-2"><div className="flex items-center gap-2">{q.origem_tipo === "pacote" && <span className="text-[9px] px-1 rounded bg-primary/15 text-primary">pacote</span>}{q.origem_tipo === "subcontratado" && <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400">sub</span>}<span className="truncate max-w-md">{q.nome}</span></div></td>{isAdmin && <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">{q.custo?.toFixed(2)}</td>}{isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={q.venda} onChange={(e) => updQuim(idx, "venda", parseFloat(e.target.value || 0))} /></td>}<td className="px-3 py-2 text-center"><TriStateToggle value={q.status} onChange={(v) => updQuim(idx, "status", v)} testidPrefix={`quim-${idx}`} /></td></tr>))}</tbody></table></div>
        )}
      </section>

      <section className="space-y-3" data-testid="block-treinamentos">
        <div><h3 className="label-tech">E. Treinamentos e capacitações</h3><p className="mt-1 text-xs text-muted-foreground">Somente itens atualmente classificados como treinamento no catálogo entram aqui. Se a categoria for corrigida em Serviços e preços, o item migra automaticamente para o bloco correto.</p></div>
        {clientGrauRisco && <p className="text-xs text-muted-foreground">CIPA (NR-05) auto-ajustada para <span className="font-mono text-foreground">{CIPA_HOURS_BY_GRAU[clientGrauRisco]}h</span> com base no grau de risco <span className="font-mono text-foreground">{clientGrauRisco}</span> do cliente.</p>}
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted/30"><tr><th className="text-left px-3 py-2 label-tech">Código</th><th className="text-left px-3 py-2 label-tech">Como aparece na proposta</th><th className="text-right px-3 py-2 label-tech">CH (h)</th>{isAdmin && <th className="text-right px-3 py-2 label-tech">Custo</th>}{isAdmin && <th className="text-right px-3 py-2 label-tech">Venda</th>}<th className="text-center px-3 py-2 label-tech">Estado</th></tr></thead>
            <tbody className="divide-y divide-border">
              {(data.treinamentos || []).map((t, idx) => {
                const isCipa = t.sigla === CIPA_SIGLA;
                return (
                  <tr key={catalogKey(t) || idx}>
                    <td className="px-3 py-2 font-mono">{t.sigla || "—"}</td>
                    <td className="px-3 py-2"><div className="font-medium">{proposalName(t) || "Treinamento sem nome"}</div>{t.descricao && t.descricao !== proposalName(t) && <div className="mt-0.5 text-xs text-muted-foreground">{t.descricao}</div>}{isCipa && clientGrauRisco && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">auto {CIPA_HOURS_BY_GRAU[clientGrauRisco]}h · GR {clientGrauRisco}</span>}</td>
                    <td className="px-3 py-2 text-right"><Input className="w-20 ml-auto text-right font-mono" type="number" min="0" value={t.carga_horaria} onChange={(e) => updItem("treinamentos", idx, "carga_horaria", parseInt(e.target.value || 0, 10))} /></td>
                    {isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={t.custo} onChange={(e) => updItem("treinamentos", idx, "custo", parseFloat(e.target.value || 0))} /></td>}
                    {isAdmin && <td className="px-3 py-2 text-right"><Input className="w-24 ml-auto text-right font-mono" type="number" step="0.01" value={t.venda} onChange={(e) => updItem("treinamentos", idx, "venda", parseFloat(e.target.value || 0))} /></td>}
                    <td className="px-3 py-2 text-center"><TriStateToggle value={t.status} onChange={(v) => updItem("treinamentos", idx, "status", v)} testidPrefix={`trein-${t.sigla || idx}`} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

